import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import moment from "moment";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";

import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const InfoRow = ({ label, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
  >
    <MDTypography
      variant="caption"
      color="text"
      fontWeight="medium"
    >
      {label}
    </MDTypography>

    <MDTypography variant="button">
      {value || "-"}
    </MDTypography>
  </Stack>
);

const DockTransferInfoDialog = ({
  open,
  onClose,
  loading,
  transferInfo,
  onRetry,
}) => {
  const status = transferInfo?.status || "unknown";

  const isFailed = status === "failed";

  const statusConfig = {
    success: {
      color: "success",
      icon: <CheckCircleIcon />,
      label: "Transferred",
    },
    failed: {
      color: "error",
      icon: <ErrorIcon />,
      label: "Failed",
    },
    pending: {
      color: "warning",
      icon: <PendingIcon />,
      label: "Pending",
    },
  };

  const current =
    statusConfig[status] || statusConfig.pending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Dock Revenue Transfer
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={6}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Status */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                {current.icon}

                <Chip
                  color={current.color}
                  label={current.label}
                  sx={{
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                />
              </Stack>
            </Paper>

            {/* Transfer Details */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                mb={2}
              >
                Transfer Details
              </MDTypography>

              <Stack spacing={1.5}>
                <InfoRow
                  label="Transferred"
                  value={
                    transferInfo?.isTransferred
                      ? "Yes"
                      : "No"
                  }
                />

                <Divider />

                <InfoRow
                  label="Retry Count"
                  value={transferInfo?.retryCount || 0}
                />

                <Divider />
                <InfoRow
                  label="Last Retry"
                  value={
                    transferInfo?.retryAt
                      ? moment(
                          transferInfo.retryAt,
                        ).format(
                          "MMM DD, YYYY hh:mm A",
                        )
                      : "-"
                  }
                />
              </Stack>
            </Paper>

            {/* Failure Reason */}
            {isFailed &&
              transferInfo?.failedReason && (
                <Alert severity="error">
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                  >
                    Transfer Failure Reason
                  </MDTypography>

                  <br />

                  {transferInfo.failedReason}
                </Alert>
              )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
        >
          Close
        </MDButton>

        {isFailed && (
          <MDButton
            color="error"
            onClick={onRetry}
          >
            Retry Transfer
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DockTransferInfoDialog;